import { useState, type ReactNode } from 'react';
import type { UserProfile } from '@erectus/shared';
import { Pill } from '@/ui/primitives.js';
import { usesImperial } from '@/lib/units.js';

export const SEX_OPTIONS: Array<{ id: NonNullable<UserProfile['sex']>; label: string }> = [
  { id: 'female', label: 'Female' },
  { id: 'male', label: 'Male' },
  { id: 'other', label: 'Another' },
  { id: 'unspecified', label: 'Prefer not to say' },
];

/** Optional. Shapes nothing today beyond context; never graded. */
export function SexField({
  value,
  onChange,
}: {
  value: UserProfile['sex'] | null;
  onChange(next: UserProfile['sex'] | null): void;
}): ReactNode {
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium">
        Sex <span className="font-normal text-muted">(optional)</span>
      </p>
      <div className="flex flex-wrap gap-2">
        {SEX_OPTIONS.map((o) => (
          <Pill key={o.id} active={value === o.id} onClick={() => onChange(value === o.id ? null : o.id)}>
            {o.label}
          </Pill>
        ))}
      </div>
    </div>
  );
}

const LB_PER_KG = 2.2046226218;

/** Optional weight in the reader's units; stored in kilograms. */
export function WeightField({
  weightKg,
  onSave,
}: {
  weightKg: number | null;
  onSave(kg: number | null): void;
}): ReactNode {
  const imperial = usesImperial();
  const [text, setText] = useState(() =>
    weightKg ? String(Math.round(imperial ? weightKg * LB_PER_KG : weightKg)) : '',
  );
  const commit = (): void => {
    if (!text) return onSave(null);
    const value = Number(text);
    if (!Number.isFinite(value)) return;
    const kg = imperial ? value / LB_PER_KG : value;
    if (kg >= 25 && kg <= 300) onSave(kg);
  };
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium" htmlFor="weight-main">
        Weight <span className="font-normal text-muted">(optional)</span>
      </label>
      <div className="flex items-center gap-2 text-sm">
        <input
          id="weight-main"
          inputMode="numeric"
          className="w-20 rounded-lg border border-line bg-white px-2 py-1.5 text-center"
          value={text}
          onChange={(e) => setText(e.target.value.replace(/[^\d.]/g, ''))}
          onBlur={commit}
          placeholder={imperial ? '160' : '70'}
        />
        {imperial ? 'lb' : 'kg'}
      </div>
    </div>
  );
}
